import { useMutation } from '@tanstack/react-query';

import {
  useRequest,
  type MutationFunction,
  type MutationFunctionContext,
  type RequestQueryObject,
} from '@frontend/api';

import type { ActionFilterQueryParams, Action } from './types';

type ActionUpdatePayload = Pick<Action, 'selectedResolution'> &
  // We want to be able to conditionally clear the assignee but not mandate it
  Partial<Pick<Action, 'assigneeId'>>;

export type BulkUpdateActionsPayload = ActionUpdatePayload & {
  filters: Omit<ActionFilterQueryParams, 'componentIds'> & {
    // In contrast to the usual action filter params, componentIds is a comma delimited string of ids
    componentIds?: string;
  };
};

// This is an async action so we only get back a taskId and the number of actions that were updated
export type BulkUpdateActionsResponse = {
  taskId: string;
  updateCount: number;
};

const getUseBulkUpdateActionsKey = () => ['rules', 'action', 'bulkUpdate'];

/**
 * Mutation function for updating an Action. The payload is applied to all actions that match the filter.
 */
export const useBulkUpdateActions: MutationFunction<
  BulkUpdateActionsResponse,
  BulkUpdateActionsPayload,
  [],
  RequestQueryObject,
  unknown
> = ({ requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useMutation<
    BulkUpdateActionsResponse,
    unknown,
    BulkUpdateActionsPayload,
    MutationFunctionContext<BulkUpdateActionsResponse>
  >({
    mutationFn: (body) =>
      request<BulkUpdateActionsResponse>('/rules/actions/bulk', {
        init: {
          method: 'POST',
          body,
        },
      }),
    ...queryOptions,
    mutationKey: getUseBulkUpdateActionsKey(),
  });
};
